import { Component, Input, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';

import { DialogService } from 'primeng/dynamicdialog';

import { datetimeFormat } from 'app/constants/date-time-format';
import { FormatService, HelperService, HTTPService, DateService, DataService } from 'app/services';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';
import { followUpSheduleStatus } from 'app/constants/marketing.constants';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-dialog-follow-up-card',
  templateUrl: './dialog-follow-up-card.component.html',
  styleUrls: ['./dialog-follow-up-card.component.scss'],
  providers: [DialogService],
})
export class DialogFollowUpCardComponent implements OnInit, OnDestroy {
  @Input() followUp: any = {};
  @Input() customer: any = {};
  @Input() isScheduled: boolean = false;
  @Input() isCloseable: boolean = true;
  @Input() isDefaultOpen: boolean = false;
  @Input() currentTime: Date;
  @Input() isMarketingOptOutEnabled: boolean = false;

  @Output() appGetFollowUp = new EventEmitter();
  @Output() appHandleOpenTemplate = new EventEmitter();
  @Output() openContactUpdateDialog = new EventEmitter();

  isScheduling: boolean = false;
  scheduleAt = {
    date: null,
    time: null,
  };
  minDate = new Date();
  isSchedulingCompleted: boolean = false;
  isResetLoading: boolean = false;
  hasCampaingStatusStopped: boolean = false;
  isScheduledCompleted: boolean = false;
  dataServiceSubscription: Subscription;
  isAutomatedFollowUpEnabled: boolean = false;
  checkFollowUpDate: boolean = false;

  dataListeners: any[] = [];

  constructor(
    private dialogService: DialogService,
    private dataService: DataService,
    private dateService: DateService,
    private httpService: HTTPService,
    private helperService: HelperService,
    private formatService: FormatService
  ) {}

  ngOnInit() {
    this.setDataService();
    if (this.dataService.isAutomatedFollowUpEnabled) {
      if (this.followUp.createdAt) this.checkFollowUpTime();
    }

    this.scheduleAt = {
      date: this.followUp.scheduleAt,
      time: this.followUp.scheduleAt,
    };

    if (this.followUp.stoppedActivity) {
      this.followUp.stoppedActivity.createdAt = this.dateService.formatDate(
        this.dateService.getCurrentDateTimeTz(new Date(this.followUp.stoppedActivity.createdAt).getTime()),
        datetimeFormat.mmmdyyhhmmaa
      );
      this.followUp.stopTooltip = `${this.followUp.stoppedActivity.actionBy} stopped this follow up at ${this.followUp.stoppedActivity.createdAt}`;
    }

    if (!this.followUp.isSend && !this.isScheduledCompleted && this.isScheduled && this.followUp.campaignStatus === followUpSheduleStatus.stop) {
      this.hasCampaingStatusStopped = true;
    }
  }

  handleOpenTemplate(templateText: any) {
    this.appHandleOpenTemplate.emit(templateText);
  }

  handleConfirmationDialog(followUp: any) {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Show Follow-up with missing date?`,
        message: `${followUp.campaignTitle} contains the "[send_estimate_date]" tag. If estimate is not sent to customer before follow-up date then date field will be empty.`,
        button: {
          success: { title: 'Schedule' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    ref.onClose.subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.handleScheduleCampaign(followUp, true);
      }
    });
  }

  openTrialCampaignDailog() {
    this.dataService.sendData(this.dataService.typeDialog('trial-campaign-dialog', 'header', null, null, { campaignType: 'followup' }));
  }

  setDataService() {
    this.dataServiceSubscription = this.dataService.getData().subscribe(({ data }) => {
      if (data) {
        if (data.key === 'company-appdata-updated') {
          this.setFlaggedData();
          this.dataServiceSubscription.unsubscribe();
        }
      }
    });
    this.setFlaggedData();
  }

  setFlaggedData() {
    this.isAutomatedFollowUpEnabled = this.dataService.isAutomatedFollowUpEnabled;
  }

  handleScheduleCampaign(followUp: any, isForcedSchedule: boolean = false) {
    if (this.dataService.marketingSettings.accessType === 'trial' && this.dataService.marketingSettings.usedFollowUps >= this.dataService.marketingSettings.followUpLimit) {
      this.openTrialCampaignDailog();
    } else {
      if (!isForcedSchedule && followUp.marketingTemplate && followUp.marketingTemplate.text && followUp.marketingTemplate.text.includes('[estimate_send_date]')) {
        this.handleConfirmationDialog(followUp);
        return;
      }

      this.isScheduling = true;
      const followUpCampaign = this.formatService.makeScheduleFollowUpData(followUp, this.customer);
      this.httpService
        .scheduleFollowUp(followUpCampaign)
        .then(
          (campaign) => {
            this.appGetFollowUp.emit();
            this.isSchedulingCompleted = true;
            this.openTrialCampaignDailog();
            this.helperService.showToaster('success', 'Follow-up scheduled');
          },
          (err) => {
            this.helperService.showToaster('error', err);
          }
        )
        .finally(() => {
          this.isScheduling = false;
        });
    }
  }

  handleOpenContactDialog() {
    this.openContactUpdateDialog.emit();
  }

  handleDeleteFollowUp(followUp: any, event: Event) {
    event.stopPropagation();

    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: 'Remove follow-up',
        message: `Are you sure you want to remove follow-up "${followUp.campaignTitle}" scheduled at ${this.dateService.formatDate(followUp.scheduleAt, datetimeFormat.mmmdyyhhmmaa)}`,
        button: {
          success: { title: 'Delete' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        this.isScheduling = true;
        this.httpService
          .deleteScheduleFollowUp(followUp.followUpId)
          .then(
            (res) => {
              this.appGetFollowUp.emit(followUp.matchId);
              this.helperService.showToaster('success', 'Successfully deleted');
            },
            (err) => {
              this.helperService.showToaster('error', err);
            }
          )
          .finally(() => {
            this.isScheduling = false;
          });
      }

      onClose.unsubscribe();
    });
  }

  handleUpdateScheduleAt(followUpId: string, close: any) {
    const date = this.dateService.formatDate(this.scheduleAt.date, datetimeFormat.yyymmdd);
    const time = this.dateService.formatDate(this.scheduleAt.time, datetimeFormat.hhmm);
    const data = { scheduleAt: `${date} ${time}` };

    let scheduleAt: Date;

    if (this.helperService.isAppleDevice) {
      scheduleAt = new Date(this.dateService.madeDateStringIOSCompatible(data.scheduleAt));
    } else {
      scheduleAt = new Date(data.scheduleAt);
    }

    if (this.currentTime.getTime() > scheduleAt.getTime()) {
      this.helperService.showToaster('error', 'Follow-up can only be scheduled for future');
      return;
    }

    if (!followUpId) {
      this.followUp.scheduleAt = scheduleAt;
    }
    if (this.isAutomatedFollowUpEnabled) {
      this.httpService.updateScheduledFollowup(followUpId, data).then(
        (res) => {
          this.followUp.scheduleAt = scheduleAt;
          this.followUp = { ...this.followUp };
          this.helperService.showToaster('success', 'Follow-up schedule updated');
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      );
    } else {
      this.httpService.updateFollowUpSchedule(followUpId, data).then(
        (res) => {
          this.followUp.scheduleAt = scheduleAt;
          this.followUp = { ...this.followUp };
          this.helperService.showToaster('success', 'Follow-up schedule updated');
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      );
    }

    close && close();
  }

  resetPreference(e, close: any) {
    e.stopPropagation();
    e.preventDefault();
    if (!this.customer._id) return;
    this.isResetLoading = true;
    this.httpService
      .resetCustomerSubscription({ customerId: this.customer._id })
      .then(
        () => {
          if (close) {
            close();
          }
          this.appGetFollowUp.emit();
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      )
      .finally(() => {
        this.isResetLoading = false;
      });
  }

  handleUpdateStatus(followUp) {
    const data = { status: followUp.campaignStatus === followUpSheduleStatus.scheduled ? followUpSheduleStatus.stop : followUpSheduleStatus.scheduled };
    this.httpService.updateScheduledFollowup(followUp.followUpId, data).then(
      (res) => {
        this.followUp.campaignStatus = data.status;
        this.followUp.statusClass = data.status.toLowerCase();
        this.hasCampaingStatusStopped = this.followUp.campaignStatus === followUpSheduleStatus.stop ? true : false;
        this.followUp = { ...this.followUp };
        if (this.followUp.campaignStatus === followUpSheduleStatus.scheduled) {
          this.followUp.stopTooltip = '';
        } else {
          this.appGetFollowUp.emit();
        }
        // this.fetchAllFollowups();
        this.helperService.showToaster('success', 'Follow-up schedule updated');
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  checkFollowUpTime() {
    const createdDate = this.dateService.getCurrentDateTimeTz(new Date(this.followUp.createdAt).getTime());
    const currentDate = this.dateService.getCurrentDateTimeTz(new Date().getTime());
    const updatedCreatedDate = this.dateService.addDaysInDatetime(new Date(createdDate), 1, 'day', datetimeFormat.mmmdyy);

    if (new Date(updatedCreatedDate) >= new Date(this.dateService.formatDate(currentDate, datetimeFormat.mmmdyy))) {
      this.checkFollowUpDate = true;
    }
  }

  ngOnDestroy() {
    if (this.dataServiceSubscription) this.dataServiceSubscription.unsubscribe();
  }
}
